import React, { useState, useRef, useEffect } from 'react';
import { MoreVertical, Edit2, Copy, Trash2, Archive, ArchiveRestore } from 'lucide-react';
import type { Questionnaire } from '../types/questionnaires';

interface QuestionnaireActionsMenuProps {
  questionnaire: Questionnaire;
  onEdit: () => void;
  onDuplicate: () => void;
  onArchive: () => void;
  onDelete: () => void;
}

const QuestionnaireActionsMenu: React.FC<QuestionnaireActionsMenuProps> = ({
  questionnaire,
  onEdit,
  onDuplicate,
  onArchive,
  onDelete
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleAction = (action: () => void) => {
    setIsOpen(false);
    action();
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        title="Aktionen"
      >
        <MoreVertical className="w-5 h-5 text-gray-600" />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[200px] z-50">
          <button
            onClick={() => handleAction(onEdit)}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
          >
            <Edit2 className="w-4 h-4" />
            Bearbeiten
          </button>
          
          <button
            onClick={() => handleAction(onDuplicate)}
            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
          >
            <Copy className="w-4 h-4" />
            Duplizieren
          </button>
          
          <div className="border-t border-gray-200 my-1"></div>
          
          {questionnaire.archived ? (
            <button
              onClick={() => handleAction(onArchive)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <ArchiveRestore className="w-4 h-4" />
              Wiederherstellen
            </button>
          ) : (
            <button
              onClick={() => handleAction(onArchive)}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3"
            >
              <Archive className="w-4 h-4" />
              Archivieren
            </button>
          )}
          
          <button
            onClick={() => handleAction(onDelete)}
            className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
          >
            <Trash2 className="w-4 h-4" />
            Löschen
          </button>
        </div>
      )}
    </div>
  );
};

export default QuestionnaireActionsMenu;

